

import React, { useState, useEffect, useMemo } from "react";
// NavLink is aliased to Link so every existing nav item automatically gains
// react-router's "active" class for the current route without touching each one.
import { useLocation, NavLink as Link } from "react-router-dom";
import {
  AddShoppingCartOutlined as AddShoppingCart,
  AdjustOutlined as Adjust,
  AcUnitOutlined as AcUnit,
  EqualizerOutlined as Equalizer,
  ReceiptOutlined as Receipt,
  CreditCardOutlined as CreditCard,
  FormatListBulletedOutlined as FormatListBulleted,
  CloudUploadOutlined as CloudUpload,
  BarChartOutlined as BarChart,
  ListAltOutlined as ListAlt,
  AssignmentReturnOutlined as AssignmentReturn,
  StorefrontOutlined as Storefront,
  PaymentOutlined as Payment,
} from "@material-ui/icons";
import { Drawer, useMediaQuery, useTheme } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import usePermissions from "../../utils/permissions";
import { useNavDrawer } from "../../Context/NavDrawerContext";
import { useShift } from "../../Context/ShiftContext";
import { useEffectiveProfile } from "../../utils/useEffectiveProfile";
import * as api from "../../api";

const useStyles = makeStyles(() => ({
  mobileDrawerPaper: {
    width: "18rem",
    backgroundColor: "var(--bg-primary, #ffffff)",
  },
}));

const NavBar = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { mobileNavOpen, setMobileNavOpen, desktopNavVisible } = useNavDrawer() || {};
  const location = useLocation();
  const [user, setUser] = useState(JSON.parse(localStorage.getItem("profile")));
  const [isInvoiceSubmenuOpen, setIsInvoiceSubmenuOpen] = useState(false);
  const [isItemsSubmenuOpen, setIsItemsSubmenuOpen] = useState(false);
  const [isStockAdjustmentSubmenuOpen, setIsStockAdjustmentSubmenuOpen] = useState(false);

  // Use permissions hook
  const { isAdmin, canReadPage, canDownloadReports } = usePermissions();
  // Employee Master/Shift Logs only make sense while this store is actually
  // using shift tracking - see Settings.js's ShiftSettings card and
  // ShiftContext.js's shiftEnabled for the same flag gating the PoS side.
  const { shiftEnabled } = useShift();
  // SuperAdmin's Global Settings > Goods Return entitlement - same
  // profile-mirror pattern as shiftEnabled above. Defaults true (visible)
  // until the profile loads, so this never flickers the link away.
  const effectiveProfile = useEffectiveProfile();
  const goodsReturnEnabled = effectiveProfile?.goodsReturnEntitled !== false;
  // isAdmin()/canReadPage() are recreated on every usePermissions() call, so
  // resolving the actual booleans here lets navListContent below memoize on
  // stable primitives instead of recomputing 30+ nav items on every render.
  const isAdminVal = isAdmin();
  const canReadInvoices = canReadPage("Invoices");
  const canReadCustomers = canReadPage("Customers");
  const canReadSuppliers = canReadPage("Suppliers");
  const canReadSupplierDues = canReadPage("Supplier Dues");
  const canReadPurchase = canReadPage("Purchase");
  const canReadStockAdjustment = canReadPage("Stock Adjustment");
  const canReadStockSummary = canReadPage("Stock Summary");
  const canReadUnits = canReadPage("Units");
  const canReadPaymentsReceipts = canReadPage("Payments & Receipts");
  const canReadItems = canReadPage("Items");
  const canReadEmployeeMaster = canReadPage("Employee Master");
  const canReadShiftLogs = canReadPage("Shift Logs");
  const canReadStaff = canReadPage("Staff");
  const canReadDashboard = canReadPage("Dashboard");
  const canReadSettings = canReadPage("Settings");
  const canReadGoodsReturns = canReadPage("Goods Returns");
  const canDownloadReportsVal = canDownloadReports();
  // SuperAdmin's Global Settings > Activity Logs toggle - hides this nav
  // link entirely when off. Authoritatively provided via effectiveProfile (useCompany)
  // and synced with GlobalSettings.
  const [activityLogsVisible, setActivityLogsVisible] = useState(
    effectiveProfile?.activityLogsVisible !== false
  );

  useEffect(() => {
    if (effectiveProfile?.activityLogsVisible !== undefined) {
      setActivityLogsVisible(effectiveProfile.activityLogsVisible !== false);
    }
  }, [effectiveProfile?.activityLogsVisible]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.getGlobalSettings();
        if (!cancelled && data?.data?.activityLogsVisible !== undefined) {
          setActivityLogsVisible(data.data.activityLogsVisible !== false);
        }
      } catch (error) {
        // Fall back to effectiveProfile value
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    setUser(JSON.parse(localStorage.getItem("profile")));
  }, [location]);

  useEffect(() => {
    if (setMobileNavOpen) setMobileNavOpen(false);
    // eslint-disable-next-line
  }, [location.pathname]);

  // Recomputing this ~30-item list (each with inline SVGs) on every render
  // was previously happening on every route change and every unrelated
  // context update - memoized so it only rebuilds when something that
  // actually affects its output changes.
  const navListContent = useMemo(() => (
    <ul className={isMobile ? "navbar-nav navbar-nav-mobile" : "navbar-nav"}>
          <li className="logo">
        
            <Link to="/PosMainScreen" className="nav-link">
              <span className="link-text logo-text">
                <img
                  style={{
                    width: "100px",
                    padding: "10px",
                    marginTop: "10px",
                  }}
                  src="https://mindtree.co.th/wp-content/uploads/mindtreetechnologies.svg"
                  alt="arc-invoice"
                />
              </span>

              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="feather feather-arrow-right"
              >
                <line x1="5" y1="12" x2="19" y2="12"></line>
                <polyline points="12 5 19 12 12 19"></polyline>
              </svg>
            </Link>
          </li>
          <li className="nav-item">
            <Link to="/PosMainScreen" className="nav-link">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="feather feather-pie-chart"
              >
                <path d="M21.21 15.89A10 10 0 1 1 8 2.83"></path>
                <path d="M22 12A10 10 0 0 0 12 2v10z"></path>
              </svg>
              <span className="link-text">Pos-Screen</span>
            </Link>
          </li>

          {(isAdminVal || canReadInvoices) && (
            <li className="nav-item">
              <div
                className={`nav-link nav-submenu-toggle${isInvoiceSubmenuOpen ? " open" : ""}`}
                onClick={() => setIsInvoiceSubmenuOpen(!isInvoiceSubmenuOpen)}
                aria-expanded={isInvoiceSubmenuOpen}
                style={{
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="feather feather-layers"
                >
                  <polygon points="12 2 2 7 12 12 22 7 12 2"></polygon>
                  <polyline points="2 17 12 22 22 17"></polyline>
                  <polyline points="2 12 12 17 22 12"></polyline>
                </svg>
                <span className="link-text">Invoices</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="nav-submenu-chevron"
                  style={{ marginLeft: "5px" }}
                >
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </div>
              <ul
                className={`nav-submenu${isInvoiceSubmenuOpen ? " nav-submenu-open" : ""}`}
                onClick={() => setIsInvoiceSubmenuOpen(!isInvoiceSubmenuOpen)}
              >
                  <li className="nav-item">
                    <Link to="/invoices" className="nav-link">
                      <Receipt />
                      <span className="link-text">All Invoices</span>
                    </Link>
                  </li>
                  <li className="nav-item">
                    <Link to="/invoices/credit" className="nav-link">
                      <CreditCard />
                      <span className="link-text">Credit Invoices</span>
                    </Link>
                  </li>
                  {goodsReturnEnabled && (isAdminVal || canReadGoodsReturns) && (
                    <li className="nav-item">
                      <Link to="/goods-returns" className="nav-link">
                        <AssignmentReturn />
                        <span className="link-text">Goods Returns</span>
                      </Link>
                    </li>
                  )}
                  {/* <li className="nav-item">
                    <Link to="/companyLogo" className="nav-link">
                      <span className="link-text">Change Logo</span>
                    </Link>
                  </li> */}
              </ul>
            </li>
          )}
          {(isAdminVal || canReadCustomers) && (
            <li className="nav-item">
              <Link to="/customers" className="nav-link">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="feather feather-users"
                >
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                  <circle cx="9" cy="7" r="4"></circle>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                </svg>
                <span className="link-text">Customers</span>
              </Link>
            </li>
          )}


{(isAdminVal || canReadSuppliers) && (
            <li className="nav-item">
              <Link to="/suppliers" className="nav-link">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="feather feather-users"
                >
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                  <circle cx="9" cy="7" r="4"></circle>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                </svg>
                <span className="link-text">Suppliers</span>
              </Link>
            </li>
          )}

            {(isAdminVal || canReadSupplierDues) && (
            <li className="nav-item">
              <Link to="/supplier-dues" className="nav-link">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="feather feather-users"
                >
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                  <circle cx="9" cy="7" r="4"></circle>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                </svg>
                <span className="link-text">Suppliers-Due</span>
              </Link>
            </li>
            )}

           {/* <li className="nav-item">
              <Link to="/cash-management" className="nav-link">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="feather feather-users"
                >
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                  <circle cx="9" cy="7" r="4"></circle>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                </svg>
                <span className="link-text">Cash-Management</span>
              </Link>
            </li>
*/}
          {isAdminVal && (
            <li className="nav-item">
              <Link to="/adminDashboard" className="nav-link">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="feather feather-user-plus"
                >
                  <path d="M16 21v-2a4 4 0 0 0-3-3.87"></path>
                  <path d="M7 21v-2a4 4 0 0 1 4-4h5"></path>
                  <circle cx="8.5" cy="7" r="4"></circle>
                  <line x1="20" y1="8" x2="20" y2="14"></line>
                  <line x1="23" y1="11" x2="17" y2="11"></line>
                </svg>
                <span className="link-text">Add User</span>
              </Link>
            </li>
          )}

          {(isAdminVal || canReadDashboard) && (
            <li className="nav-item">
              <Link to="/dashboard" className="nav-link">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="feather feather-user-plus"
                >
                  <path d="M16 21v-2a4 4 0 0 0-3-3.87"></path>
                  <path d="M7 21v-2a4 4 0 0 1 4-4h5"></path>
                  <circle cx="8.5" cy="7" r="4"></circle>
                  <line x1="20" y1="8" x2="20" y2="14"></line>
                  <line x1="23" y1="11" x2="17" y2="11"></line>
                </svg>
                <span className="link-text">Dashboard</span>
              </Link>
            </li>
          )}

          {(isAdminVal || canReadPurchase) && (
            <li className="nav-item">
              <Link to="/purchases" className="nav-link">
                <AddShoppingCart />
                <span className="link-text">Purchases</span>
              </Link>
            </li>
          )}
          {(isAdminVal || canReadStockAdjustment) && (
            <li className="nav-item">
              <div
                className={`nav-link nav-submenu-toggle${isStockAdjustmentSubmenuOpen ? " open" : ""}`}
                onClick={() => setIsStockAdjustmentSubmenuOpen(!isStockAdjustmentSubmenuOpen)}
                aria-expanded={isStockAdjustmentSubmenuOpen}
                style={{
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Adjust />
                <span className="link-text">Stock Adjustment</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="nav-submenu-chevron"
                  style={{ marginLeft: "5px" }}
                >
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </div>
              <ul
                className={`nav-submenu${isStockAdjustmentSubmenuOpen ? " nav-submenu-open" : ""}`}
                onClick={() => setIsStockAdjustmentSubmenuOpen(!isStockAdjustmentSubmenuOpen)}
              >
                  <li className="nav-item">
                    <Link to="/stockAdjustments" className="nav-link">
                      <FormatListBulleted />
                      <span className="link-text">Stock Adjustments</span>
                    </Link>
                  </li>
                  <li className="nav-item">
                    <Link to="/bulk-stock-adjustment" className="nav-link">
                      <CloudUpload />
                      <span className="link-text">Bulk Stock Upload</span>
                    </Link>
                  </li>
              </ul>
            </li>
          )}

          {(isAdminVal || canReadStockSummary) && (
            <li className="nav-item">
              <Link to="/stockDetails" className="nav-link">
                <Equalizer />
                <span className="link-text">Stock Summary</span>
              </Link>
            </li>
          )}

          {(isAdminVal || canReadPaymentsReceipts) && (
            <li className="nav-item">
              <Link to="/meetings" className="nav-link">
                <Payment />
                <span className="link-text">Payment/Receipt</span>
              </Link>
            </li>
          )}

          {(isAdminVal || canDownloadReportsVal) && (
            <li className="nav-item">
              <Link to="/reports" className="nav-link">
                <BarChart />
                <span className="link-text">Reports</span>
              </Link>
            </li>
          )}

          {(isAdminVal || canReadItems) && (
            <li className="nav-item">
              <div
                className={`nav-link nav-submenu-toggle${isItemsSubmenuOpen ? " open" : ""}`}
                onClick={() => setIsItemsSubmenuOpen(!isItemsSubmenuOpen)}
                aria-expanded={isItemsSubmenuOpen}
                style={{
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Storefront />
                <span className="link-text">Items</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="nav-submenu-chevron"
                  style={{ marginLeft: "5px" }}
                >
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </div>
              <ul
                className={`nav-submenu${isItemsSubmenuOpen ? " nav-submenu-open" : ""}`}
                onClick={() => setIsItemsSubmenuOpen(!isItemsSubmenuOpen)}
              >
                  <li className="nav-item">
                    <Link to="/ItemsList" className="nav-link">
                      <FormatListBulleted />
                      <span className="link-text">ItemsList</span>
                    </Link>
                  </li>
                  <li className="nav-item">
                    <Link to="/bulk-upload" className="nav-link">
                      <CloudUpload />
                      <span className="link-text">Bulk Add Items</span>
                    </Link>
                  </li>
              </ul>
            </li>
          )}

          {(isAdminVal || canReadUnits) && (
            <li className="nav-item">
              <Link to="/UnitsList" className="nav-link">
                <AcUnit />
                <span className="link-text">Units</span>
              </Link>
            </li>
          )}

          {(isAdminVal || canReadStaff) && (
             <li className="nav-item" style={{ display: "none" }}>
              <Link to="/staff" className="nav-link">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="feather feather-users"
                >
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                  <circle cx="9" cy="7" r="4"></circle>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                </svg>
                <span className="link-text">Staff</span>
              </Link>
            </li>
          )}

          {shiftEnabled && (isAdminVal || canReadEmployeeMaster) && (
            <li className="nav-item">
              <Link to="/employees" className="nav-link">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="feather feather-users"
                >
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                  <circle cx="9" cy="7" r="4"></circle>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                </svg>
                <span className="link-text">Employee Master</span>
              </Link>
            </li>
          )}

          {shiftEnabled && (isAdminVal || canReadShiftLogs) && (
            <li className="nav-item">
              <Link to="/shift-logs" className="nav-link">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="feather feather-clock"
                >
                  <circle cx="12" cy="12" r="10"></circle>
                  <polyline points="12 6 12 12 16 14"></polyline>
                </svg>
                <span className="link-text">Shift Logs</span>
              </Link>
            </li>
          )}

          {isAdminVal && activityLogsVisible && (
            <li className="nav-item">
              <Link to="/program-logs" className="nav-link">
                <ListAlt />
                <span className="link-text">Activity Logs</span>
              </Link>
            </li>
          )}

          <li className="nav-item">
            <Link to="/help" className="nav-link">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="feather feather-help-circle"
              >
                <circle cx="12" cy="12" r="10"></circle>
                <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
                <line x1="12" y1="17" x2="12.01" y2="17"></line>
              </svg>
              <span className="link-text">Help</span>
            </Link>
          </li>

          {(isAdminVal || canReadSettings) && (
          <li className="nav-item" id="themeButton">
            <Link to="/settings" className="nav-link">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="feather feather-settings"
              >
                <circle cx="12" cy="12" r="3"></circle>
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
              </svg>
              <span className="link-text">Settings</span>
            </Link>
          </li>
          )}
    </ul>
  ), [
    isMobile,
    isInvoiceSubmenuOpen,
    isItemsSubmenuOpen,
    isStockAdjustmentSubmenuOpen,
    isAdminVal,
    canReadInvoices,
    canReadCustomers,
    canReadSuppliers,
    canReadSupplierDues,
    canReadPurchase,
    canReadStockAdjustment,
    canReadStockSummary,
    canReadUnits,
    canReadPaymentsReceipts,
    canReadItems,
    canReadEmployeeMaster,
    canReadShiftLogs,
    canReadStaff,
    canReadDashboard,
    canReadSettings,
    canReadGoodsReturns,
    canDownloadReportsVal,
    shiftEnabled,
    activityLogsVisible,
    goodsReturnEnabled,
  ]);

  if (!user) return null;

  return (
    <div>
      {isMobile ? (
        <Drawer
          anchor="left"
          open={!!mobileNavOpen}
          onClose={() => setMobileNavOpen && setMobileNavOpen(false)}
          classes={{ paper: classes.mobileDrawerPaper }}
        >
          {navListContent}
        </Drawer>
      ) : (
        <nav
          className={`navbar ${desktopNavVisible === false ? "navbar-hidden" : ""}`}
          style={{ position: "fixed" }}
        >
          {navListContent}
        </nav>
      )}
    </div>
  );
};

export default NavBar;
